import { useEffect, useState } from "react";
import IconArrowDown from "@/app/(components)/generics/Icons/IconArrowDown";
import IconArrowUp from "@/app/(components)/generics/Icons/IconArrowUp";
import ErrorMessage from "@/app/(components)/generics/messages/ErrorMessage/ErrorMessage";
import Popover from "@/app/(components)/generics/Popover/Popover";
import PopoverContent from "@/app/(components)/generics/Popover/PopoverContent";
import PopoverTrigger from "@/app/(components)/generics/Popover/PopoverTrigger";
import { UStyles } from "@/app/utils/UStyles";
import defaultInputStyles from "../../styles/DefaultInput.module.css";
import styles from "./SelectInput.module.css";

type Props<T extends Record<string, any>> = {
  data: T;
  dataKey?: keyof T & string;
  options: { [key: string]: React.ReactNode } | [string, React.ReactNode][];
  defaultOption?: string;
  withEmptyOption?: boolean;
  withSearchBar?: {
    lang: string;
  };
  textCenter?: boolean;
  label?: string;
  labelInfo?: React.ReactNode;
  errorMessage?: string;
  className?: string;
  isPortaled?: boolean;
  onChange?: (data: T) => void;
};

export function SelectInput<T extends Record<string, any>>(props: Props<T>) {
  const {
    data,
    dataKey = "" as keyof T & string,
    options,
    defaultOption = undefined,
    withEmptyOption = false,
    withSearchBar,
    textCenter,
    label = "",
    labelInfo,
    errorMessage = "",
    className = "",
    isPortaled = false,
    onChange = async () => {},
  } = props;

  const [optionKey, setOptionKey] = useState<string>(
    defaultOption
      ? defaultOption
      : dataKey
        ? String(data[dataKey])
        : String(Object.values(data)[0]),
  );

  const [popoverVisible, setPopoverVisible] = useState<boolean>(false);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredOptions, setFilteredOptions] = useState<[string, React.ReactNode][]>([]);

  useEffect(() => {
    setOptionKey(
      defaultOption
        ? defaultOption
        : dataKey
          ? String(data[dataKey])
          : String(Object.values(data)[0]),
    );
  }, [defaultOption, data, dataKey]);

  useEffect(() => {
    const optionsEntries = Array.isArray(options) ? options : Object.entries(options);

    const filtered = optionsEntries.filter(([, optionValue]) => {
      // If it's a string, we can search. Otherwise, just include it or handle search differently.
      if (typeof optionValue === "string") {
        return optionValue.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return true;
    });

    if (withEmptyOption) {
      filtered.unshift(["", " "]);
    }

    setFilteredOptions(filtered);
  }, [searchTerm, options, withEmptyOption]);

  const handleOptionClick = async (selectedKey: string) => {
    (data as Record<string, unknown>)[dataKey] = selectedKey;

    setOptionKey(selectedKey);

    if (onChange) {
      await onChange(data);
    }

    setPopoverVisible(false);
  };

  return (
    <div className={UStyles.classNames(styles.container, className)}>
      {label && (
        <label className={styles.label}>
          {label}
          {labelInfo}
        </label>
      )}

      <Popover
        isVisible={popoverVisible}
        setIsVisible={setPopoverVisible}
        openAfterClick
        closeWhenClickOutside
        isPortaled={isPortaled}
        className={styles.popoverContainer}
      >
        <PopoverTrigger>
          <div className={styles.trigger}>
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {Array.isArray(options)
                ? options.find((o) => o[0] === optionKey)?.[1] || ""
                : (options as Record<string, React.ReactNode>)[optionKey]}
            </span>
            {popoverVisible ? <IconArrowUp /> : <IconArrowDown />}
          </div>
        </PopoverTrigger>

        <PopoverContent
          className={styles.contentWrapper}
          position={
            isPortaled ? { default: { top: "4px", left: "0px" } } : { default: { top: "55px" } }
          }
        >
          <div className={styles.select}>
            {withSearchBar?.lang && (
              <input
                type="text"
                className={styles.searchBar}
                placeholder={"Rechercher..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}

            <div className={styles.options}>
              {withSearchBar && Object.keys(filteredOptions).length === 0 ? (
                <p className={styles.notFoundText}>Pas de résultats...</p>
              ) : (
                filteredOptions.map(([optKey, optValue]) => (
                  <button
                    key={optKey}
                    className={`${styles.option} ${optionKey === optKey ? styles.optionSelect : ""}`}
                    onClick={() => handleOptionClick(optKey)}
                    style={{
                      textAlign: textCenter ? "center" : "left",
                    }}
                  >
                    {optValue}
                  </button>
                ))
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {errorMessage && (
        <div className={UStyles.classNames(styles.errorMessage, defaultInputStyles.errorMessage)}>
          <ErrorMessage message={errorMessage} />
        </div>
      )}
    </div>
  );
}
